import _ from "lodash";
import React from "react";
import { store } from "store/createStore";
import {
  FEED_CUSTOM_COMPONENT,
  FEED_TYPE,
  FEED_MODE,
  MODULES,
  PERMISSION_STATUS_TYPE,
  MODULE_CONFIGURATION_MAP,
  DP_CORE_MODULES,
  MYP_PROJECT_MODULES,
} from "./constants";
import {
  getCurriculumProgramById,
  getModulesAccessStatus,
  getModuleAccessLevels,
} from "./utils";
import {
  getFeedMode,
  getFeedDiff,
  MODULE_KEY,
  getDefaultSortOrder,
} from "./StaffPermissionsModule";
import { USER_NAME_SORT_OPTIONS_V2 } from "SchoolSetup/modules/SchoolSetupModule";
import { parseConfigurationValue } from "Utils";
import {
  ColumnTooltip,
  ModuleAccessLevelInfoPopup,
  BulkApplyColumnAction,
  BulkApplyAccessAction,
} from "StaffPermissions/commonComponents";
import i18next from "i18next";
import { CURRICULUM_MODULE_MAP } from "lib/staffPermissionMaps";
import {
  getFormattedUserName,
  getUserSortOptions,
} from "Utils/GetFormattedUserName";
import {
  PROJECT_GROUP_TYPE_COMMUNITY,
  PROJECT_GROUP_TYPE_PERSONAL,
  PROJECT_GROUP_TYPE_SERVICE_AS_ACTON,
  PROJECT_GROUP_TYPE_DP_CAS,
  PROJECT_GROUP_TYPE_DP_EE,
  PROJECT_GROUP_TYPE_DP_TOK_ESSAY,
  PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION,
  PROJECT_GROUP_TYPE_DP_G4_PROJECT,
  PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT,
  PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT,
  PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING,
  PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT,
} from "Projects/Constants/stringConstants";
import ACLStore from "lib/aclStore";

const getNameColumn = () => ({
  id: "name",
  type: "CUSTOM",
  label: "Staff name",
  locale: "common:staff_name",
  sortType: "dropdown",
  style: { minWidth: "240px" },
  hasSortingEnabled: true,
  sortByOptions: getUserSortOptions({
    type: "staff",
    sortOptions: USER_NAME_SORT_OPTIONS_V2,
    keyToMap: "sortEntity",
  }),
  entity: "NAME",
  isSticky: true,
  customComponentType: FEED_CUSTOM_COMPONENT.CUSTOM_NAME_AVATAR,
});

const getNameColumnEdit = () => ({
  ...getNameColumn(),
  hasSortingEnabled: false,
  sortType: null,
});

const DISPLAY_ROLE_COLUMN = {
  id: "displayRole",
  label: "Display Role",
  locale: "common:display_title",
  type: "CUSTOM",
  style: { minWidth: "240px", maxWidth: "280px" },
  hasSortingEnabled: true,
  entity: "DISPLAY_ROLE",
  sortType: "toggle",
  HeaderRightComponent: ColumnTooltip,
  headerRightComponentStyle: { marginLeft: "auto" },
  customComponentType: FEED_CUSTOM_COMPONENT.TEXT_FIELD,
};

const DISPLAY_ROLE_COLUMN_EDIT = {
  ...DISPLAY_ROLE_COLUMN,
  hasSortingEnabled: false,
  sortType: null,
  customComponentType: FEED_CUSTOM_COMPONENT.TEXT_EDIT_FIELD,
};

export const PERMISSIONS_COLUMN = {
  id: "permissions",
  label: "Permissions",
  locale: "common:default_access",
  type: "CUSTOM",
  customComponentType: FEED_CUSTOM_COMPONENT.PERMISSION_STATUS_VIEW,
  style: { minWidth: "300px" },
  hasSortingEnabled: true,
  sortType: "toggle",
  entity: "PERMISSIONS",
  HeaderRightComponent: ColumnTooltip,
  headerRightComponentStyle: { marginLeft: "auto" },
};

const PERMISSIONS_COLUMN_EDIT = {
  ...PERMISSIONS_COLUMN,
  customComponentType: FEED_CUSTOM_COMPONENT.PERMISSION_STATUS_DROPDOWN,
  HeaderRightComponent: BulkApplyAccessAction,
  hasSortingEnabled: false,
  sortType: null,
};

const GET_SORT_FIELD = {
  FIRST_NAME: feedItem => _.toLower(_.get(feedItem, "name.firstName")),
  LAST_NAME: feedItem => _.toLower(_.get(feedItem, "name.lastName")),
  FULL_NAME: feedItem => _.toLower(_.get(feedItem, "name.name")),
  DISPLAY_ROLE: feedItem => _.toLower(_.get(feedItem, "displayRole")),
  PERMISSIONS: feedItem => _.toLower(_.get(feedItem, "permissions")),
};

const MOUDULE_COLUMN_MIN_WIDTH = {
  [MODULES.ATTENDANCE.id]: 252,
  [MODULES.ANNOUNCEMENTS.id]: 252,
  [MODULES.EVALUATION_CYCLES.id]: 384,
  [MODULES.FAMILY_COMMUNICATION.id]: 288,
  [MODULES.PROGRESS_REPORTS.id]: 252,
  [MODULES.STUDENT_PROFILE.id]: 252,
  [MODULES.CLASS_SETTINGS.id]: 232,
  [MODULES[PROJECT_GROUP_TYPE_PERSONAL].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_COMMUNITY].id]: 264,
  [MODULES[PROJECT_GROUP_TYPE_SERVICE_AS_ACTON].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_DP_EE].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_CP_REFLECTIVE_PROJECT].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_CP_SERVICE_LEARNING].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_COMMUNITY_ENGAGEMENT].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_DP_TOK_ESSAY].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_DP_TOK_EXHIBITION].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_DP_CAS].id]: 316,
  [MODULES.SCHOOL_SETUP.id]: 252,
  [MODULES.STAFF_PERMISSIONS.id]: 300,
  [MODULES.SCHOOL_CALENDAR.id]: 252,
  [MODULES.ACADEMIC_SETUP.id]: 252,
  [MODULES.EDUCATOR_CENTER.id]: 252,
  [MODULES.UNIT_PLANNING.id]: 252,
  [MODULES.CURRICULUM_SETTINGS.id]: 272,
  [MODULES.PLANNING_INSIGHTS.id]: 252,
  [MODULES.CLASS_SUMMARY.id]: 252,
  [MODULES.IBIS_REGISTRATION.id]: 252,
  [MODULES.IB_ECOURSEWORK.id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_DP_G4_PROJECT].id]: 252,
  [MODULES[PROJECT_GROUP_TYPE_DP_COLLABORATIVE_SCIENCES_PROJECT].id]: 252,
  [MODULES.QUESTION_LIBRARY.id]: 288,
  [MODULES.BEHAVIOUR_MANAGEMENT.id]: 300,
};

const getModuleColumnStyle = ({ module, isDisabled }) => {
  const minWidth =
    (MOUDULE_COLUMN_MIN_WIDTH[module] || 0) + (isDisabled ? 40 : 0);
  return {
    minWidth: `${minWidth}px`,
  };
};

export const getModuleColumnStyleMemoized = _.memoize(
  params => getModuleColumnStyle(params),
  params => JSON.stringify(params)
);

const isModuleShouldSortAlphabetically = _.memoize(module => {
  return !_.includes([...MYP_PROJECT_MODULES, ...DP_CORE_MODULES], module);
}, JSON.stringify);

export const getSortedCurriculumModules = _.memoize(moduleIds => {
  const [sortableModules, restModules] = _.partition(
    moduleIds,
    isModuleShouldSortAlphabetically
  );
  const sortedModules = _.orderBy(
    sortableModules,
    moduleId => _.toLower(i18next.t(MODULES[moduleId].label)),
    "asc"
  );
  return [...sortedModules, ...restModules];
});

const getAdminRoleAccessFeedColumns = ({
  mode = FEED_MODE.VIEW,
  onRowClick,
}) => {
  const isEditMode = mode === FEED_MODE.EDIT;

  const ADMIN_FEED_NAME_COLUMN = {
    ...(isEditMode ? getNameColumnEdit() : getNameColumn()),
    onRowClick,
    // style: { maxWidth: "50%" },
  };

  const ADMIN_DISPLAY_ROLE_COLUMN = {
    ...(isEditMode ? DISPLAY_ROLE_COLUMN_EDIT : DISPLAY_ROLE_COLUMN),
    style: { minWidth: "240px", width: "calc((100vw - 376px)/2)" },
  };

  return [ADMIN_FEED_NAME_COLUMN, ADMIN_DISPLAY_ROLE_COLUMN];
};

const getModulePermissionsValue = ({
  perm,
  modulePerm,
  configurations,
  moduleAvailabilityPerm,
}) => {
  let moduleAvailable = true;
  let isDisabled = false;
  if (perm) {
    const modulePermConfig = _.find(configurations, {
      configuration: perm,
    });

    moduleAvailable = parseConfigurationValue(modulePermConfig);
  }
  if (moduleAvailabilityPerm) {
    const modulePermConfig = _.find(configurations, {
      configuration: moduleAvailabilityPerm,
    });
    moduleAvailable =
      moduleAvailable && parseConfigurationValue(modulePermConfig);
  }

  if (modulePerm) {
    const moduleConfig = _.find(configurations, {
      configuration: modulePerm,
    });
    isDisabled = !parseConfigurationValue(moduleConfig);
  }
  return { isDisabled, isModuleAvailable: moduleAvailable };
};

const getCustomColumnFields = ({
  moduleId,
  behaviourNomenclatureConstants,
}) => {
  switch (moduleId) {
    case MODULES.BEHAVIOUR_MANAGEMENT.id: {
      return {
        label: behaviourNomenclatureConstants.MODULE,
        locale: undefined,
      };
    }
    default: {
      return {};
    }
  }
};

const getTeacherRoleAccessFeedColumns = ({
  curriculumProgramId,
  mode = FEED_MODE.VIEW,
  containerRef,
  onRowClick,
  configurations,
  behaviourNomenclatureConstants,
}) => {
  const { type: curriculumType, acronym } = getCurriculumProgramById(
    curriculumProgramId
  );
  const isEditMode = mode === FEED_MODE.EDIT;

  const TEACHER_FEED_NAME_COLUMN = {
    ...(isEditMode ? getNameColumnEdit() : getNameColumn()),
    onRowClick,
  };

  const PERMISSION_COLUMN_FIELD = {
    ...(isEditMode ? PERMISSIONS_COLUMN_EDIT : PERMISSIONS_COLUMN),
    isEditMode,
    metadata: {
      curriculumAcronym: acronym,
      curriculumType,
    },
  };

  const columns = isEditMode
    ? [
        TEACHER_FEED_NAME_COLUMN,
        DISPLAY_ROLE_COLUMN_EDIT,
        PERMISSION_COLUMN_FIELD,
      ]
    : [TEACHER_FEED_NAME_COLUMN, DISPLAY_ROLE_COLUMN, PERMISSION_COLUMN_FIELD];

  const moduleColumns = [];

  const curriculumModules =
    CURRICULUM_MODULE_MAP[curriculumType]?.modules || [];

  const sortedCurriculumModules = getSortedCurriculumModules(curriculumModules);

  _.forEach(sortedCurriculumModules, module => {
    const {
      id,
      label,
      perm,
      modulePerm,
      moduleAvailabilityPerm,
      shouldHideModuleColumn = _.stubFalse,
    } = MODULES[module];

    const { isDisabled, isModuleAvailable } = getModulePermissionsValue({
      perm,
      modulePerm,
      configurations,
      moduleAvailabilityPerm,
    });

    if (!isModuleAvailable || shouldHideModuleColumn(ACLStore)) {
      return;
    }
    moduleColumns.push({
      id: id, // ensure that all the module columns' ids follow the convention of declaring constants strings, ie. ANNOUNCEMENTS, EVALUATION_CYCLES
      label: id,
      locale: label,
      type: "CUSTOM",
      isDisabled,
      customComponentType: isEditMode
        ? FEED_CUSTOM_COMPONENT.MODULE_ACCESS_DROPDOWN
        : FEED_CUSTOM_COMPONENT.MODULE_ACCESS_VIEW,
      style: getModuleColumnStyleMemoized({ module: id, isDisabled }),
      hasSortingEnabled: !isEditMode,
      sortType: isEditMode ? null : "toggle",
      entity: id,
      metadata: {
        curriculumAcronym: acronym,
        curriculumType,
      },
      isEditMode,
      headerRightComponentStyle: { marginLeft: "auto", display: "flex" },
      HeaderRightComponent: isEditMode
        ? props => (
            <BulkApplyColumnAction
              {...props}
              behaviourNomenclatureConstants={behaviourNomenclatureConstants}
            />
          )
        : rest => (
            <ModuleAccessLevelInfoPopup
              containerRef={containerRef}
              isDisabled={isDisabled}
              behaviourNomenclatureConstants={behaviourNomenclatureConstants}
              {...rest}
            />
          ),
      ...getCustomColumnFields({
        moduleId: id,
        behaviourNomenclatureConstants,
      }),
    });
  });

  return [...columns, ...moduleColumns];
};

export const getFeedColumns = ({
  feedKey,
  curriculumProgramId,
  containerRef,
  onRowClick,
  configurations,
  behaviourNomenclatureConstants,
}) => {
  const mode = getFeedMode({ feedKey, curriculumProgramId });

  switch (feedKey) {
    case FEED_TYPE.ADMINISTRATORS:
      return getAdminRoleAccessFeedColumns({ mode, onRowClick });
    case FEED_TYPE.TEACHERS:
      return getTeacherRoleAccessFeedColumns({
        curriculumProgramId,
        mode,
        containerRef,
        onRowClick,
        configurations,
        behaviourNomenclatureConstants,
      });
  }
};

export const isModuleColumn = name => {
  return /^[A-Z,0-9]+(?:_[A-Z,0-9]+)*$/.test(name); // reference - https://regex101.com/r/lO6kiY/1
};

export const getModuleAccessDisplayOrders = feedRow => {
  const moduleColumns = _.filter(_.keys(feedRow), isModuleColumn);
  const moduleColumnsData = _.pick(feedRow, moduleColumns);
  const displayOrder = {};

  _.forEach(moduleColumnsData, (accessValue, module) => {
    const moduleAccessLevels = getModuleAccessLevels(module);

    const accessDisplayOrder = _.get(
      _.find(moduleAccessLevels, { id: accessValue }),
      "displayOrder"
    );

    displayOrder[module] = accessDisplayOrder;
  });

  return displayOrder;
};

export const updatePermissionStateInFeedData = ({ feedData }) => {
  return _.map(feedData, feedRow => {
    const columnIds = _.keys(feedRow);
    const moduleColumnIds = _.filter(columnIds, isModuleColumn);
    const moduleColumns = _.pick(feedRow, moduleColumnIds);
    moduleColumns.disabledColumns = _.get(feedRow, "disabledColumns", []);
    const curriculumDefaultAccess = _.get(feedRow, "curriculumDefaultAccess");
    const isDefaultAccessAdmin = _.isEqual(
      curriculumDefaultAccess,
      "ADMIN_ACCESS"
    );
    const modulesAccessStatus = getModulesAccessStatus(moduleColumns);
    const isTeacherAccess = !_.some(modulesAccessStatus, [
      "hasDefaultAccess",
      false,
    ]);
    const isAdminAccess = !_.some(modulesAccessStatus, [
      "hasHighestAccess",
      false,
    ]);

    return {
      ...feedRow,
      permissions: isTeacherAccess
        ? PERMISSION_STATUS_TYPE.TEACHER
        : isAdminAccess
        ? PERMISSION_STATUS_TYPE.ADMIN
        : isDefaultAccessAdmin
        ? PERMISSION_STATUS_TYPE.ADMIN_CUSTOM
        : PERMISSION_STATUS_TYPE.TEACHER_CUSTOM,
    };
  });
};

const feedDataDisplayOrderEnhancer = ({ feedData, feedKey }) => {
  if (feedKey === FEED_TYPE.ADMINISTRATORS) {
    return _.map(feedData, feedRow => {
      return {
        ...feedRow,
        displayOrder: {},
      };
    });
  }

  if (feedKey === FEED_TYPE.TEACHERS) {
    return _.map(feedData, feedRow => {
      return {
        ...feedRow,
        displayOrder: getModuleAccessDisplayOrders(feedRow),
      };
    });
  }
};

export const getServerFeedData = ({
  staffData,
  feedKey,
  curriculumProgramId,
  teacherConfigurations,
}) => {
  let feedData = [];
  if (feedKey === FEED_TYPE.ADMINISTRATORS) {
    feedData = _.map(staffData, staff => {
      const {
        id,
        firstName,
        middleName,
        lastName,
        email,
        profileImage,
        displayRole,
      } = staff;
      return {
        id,
        name: {
          id,
          name: getFormattedUserName({ user: staff, type: "staff" }),
          profileImage,
          email,
          firstName,
          middleName,
          lastName,
          shouldDynamicColor: true,
        },
        displayRole: displayRole,
      };
    });
  }

  if (feedKey === FEED_TYPE.TEACHERS) {
    const { type: curriculumType } = getCurriculumProgramById(
      curriculumProgramId
    );

    const curriculumModules =
      CURRICULUM_MODULE_MAP[curriculumType]?.modules || [];
    feedData = _.reduce(
      staffData,
      (result, staff) => {
        const {
          id,
          firstName,
          middleName,
          lastName,
          email,
          profileImage,
          configurations,
          displayRole,
          curriculumDefaultAccess,
        } = staff;

        const isDefaultAccessAdmin = _.isEqual(
          curriculumDefaultAccess,
          "ADMIN_ACCESS"
        );

        const moduleColumnsData = { disabledColumns: [] };
        const cellStyles = {};
        _.forEach(configurations, config => {
          const moduleId = _.findKey(
            MODULE_CONFIGURATION_MAP,
            configValue => configValue == config.configuration
          );

          if (!moduleId) return;

          if (!_.includes(curriculumModules, moduleId)) return;

          const {
            perm,
            modulePerm,
            shouldHideModuleColumn = _.stubFalse,
          } = MODULES[moduleId];

          const { isDisabled, isModuleAvailable } = getModulePermissionsValue({
            perm,
            modulePerm,
            configurations: teacherConfigurations,
          });

          if (!isModuleAvailable || shouldHideModuleColumn(ACLStore)) {
            return;
          }
          if (isDisabled) {
            moduleColumnsData.disabledColumns = [
              ...moduleColumnsData.disabledColumns,
              moduleId,
            ];
          }

          moduleColumnsData[moduleId] = parseConfigurationValue(config);
          if (
            getFeedMode({ feedKey, curriculumProgramId }) === FEED_MODE.EDIT
          ) {
            cellStyles[moduleId] = {
              padding: 0,
            };
          }
        });

        const modulesAccessStatus = getModulesAccessStatus(moduleColumnsData);
        const isTeacherAccess = !_.some(modulesAccessStatus, [
          "hasDefaultAccess",
          false,
        ]);
        const isAdminAccess = !_.some(modulesAccessStatus, [
          "hasHighestAccess",
          false,
        ]);

        result.push({
          id,
          name: {
            id,
            name: getFormattedUserName({ user: staff, type: "staff" }),
            profileImage,
            email,
            firstName,
            middleName,
            lastName,
            shouldDynamicColor: true,
          },
          displayRole: displayRole,
          curriculumDefaultAccess: curriculumDefaultAccess,
          permissions: isTeacherAccess
            ? PERMISSION_STATUS_TYPE.TEACHER
            : isAdminAccess
            ? PERMISSION_STATUS_TYPE.ADMIN
            : isDefaultAccessAdmin
            ? PERMISSION_STATUS_TYPE.ADMIN_CUSTOM
            : PERMISSION_STATUS_TYPE.TEACHER_CUSTOM,
          ...moduleColumnsData,
          cellStyles,
        });
        return result;
      },
      []
    );
  }

  const feedDataWithDisplayRole = feedDataDisplayOrderEnhancer({
    feedData,
    feedKey,
  });
  return feedDataWithDisplayRole;
};

export const getFilteredFeedData = ({ feedData }) => {
  const state = store?.getState() ?? {};
  const searchText = _.get(state, `${MODULE_KEY}.filters.searchText`);
  const filterBySearchText = feedRow => {
    const {
      name: { name, email },
      displayRole,
    } = feedRow;

    if (_.isEmpty(searchText)) return true;

    const searchTextValuesToMatch = _.map(
      [name, email, displayRole],
      _.toLower
    );

    return _.some(searchTextValuesToMatch, value =>
      _.includes(value, _.toLower(searchText))
    );
  };

  return _.chain(feedData).filter(filterBySearchText).value();
};

export const getSortedFeedData = ({
  feedData,
  feedKey,
  curriculumProgramId,
}) => {
  const state = store?.getState() ?? {};
  let sortedFieldsWithOrder = _.get(state, `${MODULE_KEY}.sortFields`);

  if (_.isEmpty(sortedFieldsWithOrder)) {
    sortedFieldsWithOrder = getDefaultSortOrder();
  }
  const sortKey = _.first(_.keys(sortedFieldsWithOrder));

  const { direction, menuItemEntity } = sortedFieldsWithOrder[sortKey];

  if (
    feedKey === FEED_TYPE.TEACHERS &&
    _.isUndefined(GET_SORT_FIELD[menuItemEntity])
  ) {
    if (!isModuleColumn(sortKey)) {
      return feedData;
    }
    // additionally check whether menuItemEntity is valid for current curriculum type teacher feed
    const { type: curriculumType } = getCurriculumProgramById(
      curriculumProgramId
    );
    const isModuleValidForCurriculum = _.includes(
      CURRICULUM_MODULE_MAP[curriculumType].modules,
      sortKey
    );
    if (!isModuleValidForCurriculum) return feedData;
  }

  let sortFieldGetter = GET_SORT_FIELD[menuItemEntity];

  if (!sortFieldGetter) {
    // sort key is module name
    sortFieldGetter = feedItem => {
      return _.get(feedItem, ["displayOrder", menuItemEntity]);
    };
  }

  const sortedFeed = _.orderBy(feedData, sortFieldGetter, _.toLower(direction));

  return sortedFeed;
};

export const getMergedFeedDataWithDiff = ({
  feedData,
  feedKey,
  curriculumProgramId,
  feedDataMiddleWare,
}) => {
  const feedMode = getFeedMode({ feedKey, curriculumProgramId });

  // in view mode, no need to merge diff with server feed data
  if (feedMode === FEED_MODE.VIEW) {
    return feedData;
  }

  const currentFeedDiff = getFeedDiff({ feedKey, curriculumProgramId });
  const clonedFeedData = _.cloneDeep(feedData);
  _.forEach(currentFeedDiff, changedItem => {
    const { id, ...updatedValue } = changedItem;
    const feedItemIndex = _.findIndex(clonedFeedData, { id });
    clonedFeedData[feedItemIndex] = {
      ...clonedFeedData[feedItemIndex],
      ...updatedValue,
    };
  });

  const isMiddleWareProvided = typeof feedDataMiddleWare === "function";
  const updatedFeedData = isMiddleWareProvided
    ? feedDataMiddleWare({ feedData: clonedFeedData })
    : clonedFeedData;

  return updatedFeedData;
};

export const getFeedData = ({ staffData, feedKey, curriculumProgramId }) => {
  const filteredFeedData = getFilteredFeedData({
    feedData: staffData,
    feedKey,
    curriculumProgramId,
  });
  const mergedFeedData = getMergedFeedDataWithDiff({
    feedData: filteredFeedData,
    feedKey,
    curriculumProgramId,
    feedDataMiddleWare:
      feedKey === FEED_TYPE.TEACHERS
        ? updatePermissionStateInFeedData
        : undefined,
  });

  return { serverFeedData: staffData, updatedFeedData: mergedFeedData };
};
